import AxiosInstance from "../config/AxiosInstance";
import AxiosInstanceFormData from "../config/AxiosInstanceFormData";

export interface PayrollPaymentDetailRow {
  id: string;
  payrollPaymentId: string;
  rowNo: number;
  employeeId?: string | null;
  firstName: string;
  lastName: string;
  employeeName?: string | null;
  bankAccountNumber: string;
  amount: number;
  description?: string | null;
  glDescription?: string | null;
  isMatchedToEmployee: boolean;
  matchNote?: string | null;
  isDeleted?: boolean;
}

export interface PayrollPayment {
  id: string;
  companyId: string;
  locationId?: string | null;
  transactionMonthsId: string;
  paymentDate: string;
  payrollPeriodYear: number;
  payrollPeriodMonth: number;
  description?: string | null;
  sourceFileName?: string | null;
  sourceFileUploadedAt?: string | null;
  bankChartOfAccountId: string;
  bankSystemAccountId?: string | null;
  salaryPayableChartOfAccountId: string;
  salaryPayableSystemAccountId?: string | null;
  totalAmount: number;
  lineCount: number;
  statusId: number;
  importHash?: string | null;
  referenceNo?: string | null;
  isDeleted: boolean;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  details?: PayrollPaymentDetailRow[];
}

export interface ImportPayrollPayload {
  transactionMonthsId: string;
  paymentDate?: string;
  payrollPeriodYear?: number;
  payrollPeriodMonth?: number;
  description?: string;
  bankChartOfAccountId?: string;
  salaryPayableChartOfAccountId?: string;
}

export interface ImportResult {
  id: string;
  lineCount: number;
  totalAmount: number;
}

const importCsv = async (file: File, payload: ImportPayrollPayload): Promise<ImportResult> => {
  const form = new FormData();
  form.append("file", file);
  form.append("transactionMonthsId", payload.transactionMonthsId);
  if (payload.paymentDate != null && payload.paymentDate !== "") {
    form.append("paymentDate", payload.paymentDate);
  }
  if (payload.payrollPeriodYear != null) {
    form.append("payrollPeriodYear", String(payload.payrollPeriodYear));
  }
  if (payload.payrollPeriodMonth != null) {
    form.append("payrollPeriodMonth", String(payload.payrollPeriodMonth));
  }
  if (payload.description != null && payload.description !== "") {
    form.append("description", payload.description);
  }
  if (payload.bankChartOfAccountId != null && payload.bankChartOfAccountId !== "") {
    form.append("bankChartOfAccountId", payload.bankChartOfAccountId);
  }
  if (payload.salaryPayableChartOfAccountId != null && payload.salaryPayableChartOfAccountId !== "") {
    form.append("salaryPayableChartOfAccountId", payload.salaryPayableChartOfAccountId);
  }

  const { data } = await AxiosInstanceFormData.post(
    "/api/finex/payroll-payments/import-csv",
    form
  );
  return data;
};

/** Get default bank and salary COA IDs for the company (for Declaration modal). */
const getDefaultCoas = async (): Promise<{ bankChartOfAccountId: string; salaryPayableChartOfAccountId: string }> => {
  const { data } = await AxiosInstance.get("/api/finex/payroll-payments/default-coas");
  return data;
};

const getAll = async (
  params?: { statusId?: number; locationId?: string; page?: number; pageSize?: number }
): Promise<{ data: PayrollPayment[]; total: number }> => {
  const { data } = await AxiosInstance.get("/api/finex/payroll-payments", {
    params: {
      page: params?.page ?? 1,
      pageSize: params?.pageSize ?? 50,
      statusId: params?.statusId,
      locationId: params?.locationId,
    },
  });
  return data;
};

const getOne = async (id: string): Promise<PayrollPayment> => {
  const { data } = await AxiosInstance.get(`/api/finex/payroll-payments/${id}`);
  return data;
};

const confirm = async (id: string): Promise<PayrollPayment> => {
  const { data } = await AxiosInstance.post(`/api/finex/payroll-payments/${id}/confirm`);
  return data;
};

const cancel = async (id: string): Promise<PayrollPayment> => {
  const { data } = await AxiosInstance.post(`/api/finex/payroll-payments/${id}/cancel`);
  return data;
};

const remove = async (id: string): Promise<void> => {
  await AxiosInstance.delete(`/api/finex/payroll-payments/${id}`);
};

export { importCsv, getDefaultCoas, getAll, getOne, confirm, cancel, remove };
